/**
 * Skad weszlo sie do karty kontrahenta - zeby "Powrot" wracal TAM, a nie
 * zawsze na liste Kontrahentow (uwaga 2026-09-23: z "Najblizszych" karta
 * odsylala na liste i trzeba bylo wchodzic w "Najblizszych" od nowa).
 *
 * Obszar karty to /employee/partner/:id i jej podstrony (sprzedaz, faktury,
 * sprawy, produkty...). Podstrony wracaja do karty przez router.push, wiec
 * samo "wstecz w historii" po wizycie w podstronie trafialoby z powrotem w nia.
 * Dlatego zrodlo zapamietujemy przy WEJSCIU w obszar danego kontrahenta,
 * a ruch wewnatrz obszaru go nie nadpisuje.
 *
 * Pamiec zyje do przeladowania aplikacji - po odswiezeniu (albo wejsciu
 * z linku) zrodla nie ma i "Powrot" prowadzi na liste, jak dotad.
 */
import { reactive } from 'vue';
import type { Router } from 'vue-router';

const LISTA = '/employee/partners';
const NAJBLIZSI = '/employee/nearby-partners';
const OBSZAR = /^\/employee\/partner\/([^/?#]+)/;

/** id kontrahenta -> fullPath strony, z ktorej weszlo sie w jego obszar */
const zrodla = reactive(new Map<string, string>());

export function idKontrahentaZTrasy(path: string): string | null {
  return OBSZAR.exec(path)?.[1] ?? null;
}

/**
 * Po kazdej udanej nawigacji. `fromZAplikacji` = poprzednia trasa istniala
 * (false przy starcie aplikacji / odswiezeniu strony).
 */
export function zapamietajWejscie(toPath: string, fromPath: string, fromFullPath: string, fromZAplikacji: boolean): void {
  const id = idKontrahentaZTrasy(toPath);
  if (!id) return;
  if (idKontrahentaZTrasy(fromPath) === id) return; // karta <-> podstrony tego samego kontrahenta
  if (fromZAplikacji && fromPath.startsWith('/employee/')) zrodla.set(id, fromFullPath);
  else zrodla.delete(id);
}

export function zrodloWejscia(id: string): string | null {
  return zrodla.get(id) ?? null;
}

export type Powrot = { rodzaj: 'wstecz' } | { rodzaj: 'przejdz'; sciezka: string };

/**
 * Dokad prowadzi "Powrot" z karty. `poprzedniaWHistorii` = history.state.back
 * routera (per SPA - w iframe pulpitu window.history jest wspolna z calym oknem).
 */
export function celPowrotu(id: string, poprzedniaWHistorii: string | null | undefined): Powrot {
  const zrodlo = zrodloWejscia(id);
  if (!zrodlo) return { rodzaj: 'przejdz', sciezka: LISTA };
  // prosto ze zrodla: cofniecie - Ionic odda strone w stanie sprzed wejscia
  // (lista, suwak, adres), bez ponownego ladowania
  if (poprzedniaWHistorii === zrodlo) return { rodzaj: 'wstecz' };
  // po podstronach cofniecie trafiloby w podstrone; nowa nawigacja do zrodla
  // (zmiany z podstron i tak wymagaja swiezego montazu - lekcja z 21.09)
  return { rodzaj: 'przejdz', sciezka: zrodlo };
}

/** Napis na przycisku powrotu - mowi, dokad prowadzi. */
export function opisPowrotu(id: string): string {
  const zrodlo = zrodloWejscia(id);
  if (!zrodlo || zrodlo === LISTA || zrodlo.startsWith(LISTA + '?')) return 'Powrót do listy';
  if (zrodlo === NAJBLIZSI || zrodlo.startsWith(NAJBLIZSI + '?')) return 'Powrót do najbliższych';
  return 'Powrót';
}

export function zainstalujPamiecWejsc(router: Router): void {
  router.afterEach((to, from, failure) => {
    if (failure) return; // przerwana nawigacja (guard zwrocil false) - nikt nie wszedl na karte
    zapamietajWejscie(to.path, from.path, from.fullPath, from.matched.length > 0);
  });
}

/** Tylko dla testow. */
export function _wyczyscZrodla(): void {
  zrodla.clear();
}
